"use client";

import React, { FC } from "react";

interface OutletLocationProps {
  /** City of the outlet holding this product, e.g. "Bengaluru". */
  city?: string;
  /** Branch name, used as the tooltip so the card itself stays short. */
  outletName?: string;
  className?: string;
}

/**
 * "Ships from <city>" line for a product card.
 *
 * Only rendered by grids that have established the store actually has more than
 * one outlet — see `hasMultipleOutlets`. On a single-branch store this line is
 * identical on every card, so it is pure noise and is left off entirely.
 *
 * Deliberately the CITY, not the branch name: "Bengaluru" answers the buyer's
 * real question (how far away is this) while "SUGANDHI Bengaluru" mostly repeats
 * the store name they are already looking at. The branch name is the title
 * attribute for anyone who wants it.
 */
export const OutletLocation: FC<OutletLocationProps> = ({ city, outletName, className }) => {
  if (!city) return null;

  return (
    <span
      className={`inline-flex items-center gap-1 ${className ?? ""}`}
      title={outletName || city}
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: "var(--techno-black-50)",
        letterSpacing: 0,
        // The row sits inside a flex column with other card meta; keep it from
        // stretching the card when a city name is long.
        minWidth: 0,
      }}
    >
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        style={{ flexShrink: 0 }}
      >
        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
      <span className="truncate">{city}</span>
    </span>
  );
};

/**
 * True when a rendered list spans more than one outlet.
 *
 * Derived from the list itself rather than threaded down as a prop so every grid
 * gets the behaviour without the section registry having to know about outlets.
 * A filter that narrows results to a single branch correctly drops the label —
 * at that point every card ships from the same place and saying so adds nothing.
 */
export function hasMultipleOutlets(products: { outletId?: string }[]): boolean {
  const seen = new Set<string>();
  for (const p of products) {
    if (p.outletId) seen.add(p.outletId);
    if (seen.size > 1) return true;
  }
  return false;
}
